import type { ErrorEvent } from '@sentry/core';
import { PRODUCTION } from './environment';

/**
 * beforeSend hook to improve error title displayed in sentry
 */
function beforeSend(event: ErrorEvent): ErrorEvent | null {
  // Prefix error title with [ServiceName] for decorator-caught errors (cron jobs, etc.)
  if (event.extra?.['serviceContext'] && event.exception?.values?.[0]) {
    const fullContext = String(event.extra['serviceContext']);
    const serviceName = fullContext.split('.')[0]; // Extract ServiceName from "ServiceName.methodName"
    const originalValue = event.exception.values[0].value || '';

    if (!originalValue.startsWith('[')) {
      event.exception.values[0].value = `[${serviceName}] ${originalValue}`;
    }

    event.tags = { ...event.tags, service: serviceName };
  }

  // Prefix error title with [ErrorCode] for our HTTP exceptions (e.g. [UniqueConstraintFailed] Bad Request)
  if (event.tags?.['errorCode'] && event.exception?.values?.[0]) {
    const errorCode = String(event.tags['errorCode']);
    const originalValue = event.exception.values[0].value || '';

    if (!originalValue.startsWith('[')) {
      event.exception.values[0].value = `[${errorCode}] ${originalValue}`;
    }
  }

  return event;
}

/**
 * Returns Sentry settings with sample rates based on environment.
 *
 * Production: 5% tracing/profiling. Non-production: 20%. All errors are
 * always captured.
 */
export function getSentrySettings(environment: string) {
  const isProduction = environment === PRODUCTION;

  return {
    debug: false,

    attachStacktrace: true,
    sendClientReports: true,

    // Always capture all errors
    sampleRate: 1.0,

    tracesSampleRate: isProduction ? 0.05 : 0.2,
    profileSessionSampleRate: isProduction ? 0.05 : 0.2,

    beforeSend,
  };
}
